import { useEffect, useRef } from "react";
import consts from "shared/consts";

interface PerformanceMetrics {
  count: number;
  totalRenderTime: number;
}

const useKonvaPerformanceMetrics = (enable: boolean, ref: React.RefObject<any>) => {
  // Ref to store aggregated metrics
  const aggregatedMetrics = useRef<Record<string, PerformanceMetrics>>({});

  useEffect(() => {
    if (!enable) return;
    if (!ref.current) return;

    const stage = ref.current.getStage();

    // Initialize the aggregated metrics if not already initialized
    if (Object.keys(aggregatedMetrics.current).length === 0) {
      // Initialize metrics using CANVAS_ELEMENTS values
      aggregatedMetrics.current = Object.values(consts.CANVAS_ELEMENTS).reduce((acc, type) => {
        acc[type] = { count: 0, totalRenderTime: 0 };
        return acc;
      }, {} as Record<string, PerformanceMetrics>);
    }

    // Function to update metrics
    const updateMetrics = () => {
      Object.values(consts.CANVAS_ELEMENTS).forEach((type) => {
        const nodes = stage.find((node: any) => {
          return node.attrs.id?.startsWith(type);
        });
        nodes.forEach((node: any) => {
          const start = performance.now();
          node.draw();
          const end = performance.now();
          const renderTime = end - start;

          // Aggregate metrics
          aggregatedMetrics.current[type].count += 1;
          aggregatedMetrics.current[type].totalRenderTime += renderTime;
        });
      });
    };

    // Function to log metrics every 5 seconds
    const logMetrics = () => {
      const sortedMetrics = Object.entries(aggregatedMetrics.current)
        .filter(([, metrics]) => metrics.count > 0) // Only include types with at least one node
        .map(([type, metrics]) => ({
          type,
          count: metrics.count,
          totalRenderTime: metrics.totalRenderTime,
          averageRenderTime: metrics.totalRenderTime / metrics.count,
        }))
        .sort((a, b) => b.averageRenderTime - a.averageRenderTime); // Sort by average render time (descending)

      // Log the metrics
      console.log(`--- Performance Metrics Log ---`);
      sortedMetrics.forEach(({ type, count, totalRenderTime, averageRenderTime }) => {
        console.log(
          `Type: ${type} | Count: ${count} | Total Render Time: ${totalRenderTime.toFixed(
            3
          )} ms | Average Render Time: ${averageRenderTime.toFixed(3)} ms`
        );
      });
    };

    // Start the interval to update metrics and log every 5 seconds
    const intervalId = setInterval(() => {
      updateMetrics();
      logMetrics();
    }, 5000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [enable]); // Empty dependency array ensures it only runs once when the component mounts
};

export default useKonvaPerformanceMetrics;
